import React from 'react';
import { useTranslation } from 'react-i18next';
import { SelectButton, Button, Icon, Modal, Tooltip, Card } from '@weezevent/nacre';
import RatesManager, { generateClassNameRateName, SortRatesButton } from '../../rates/components/rateManager';
import LoadingCard from '../../../components/loadingCard';

import { InheritedProperty } from '../../rates/ratesList';
import StyleRM from '../../rates/components/rateManager.module.css';
import RateModel from '../../../models/rateModel';
import Style from './ratesPicker.module.css';

export default function RatesPicker({ organization = null, event = null, linkedRates = [], setForm, waitingListId }) {
    const { t } = useTranslation();
    const [show, setShow] = React.useState(false);
    const [selectedRatesAdd, setSelectedRatesAdd] = React.useState([]);
    const [selectedRatesDelete, setSelectedRatesDelete] = React.useState([]);

    const onUpdateRates = React.useCallback(
        newValue => {
            const _rates = newValue.map(rate => (rate.object ? rate.object.id : rate));
            setForm('rates', _rates);
            setSelectedRatesAdd([]);
            setSelectedRatesDelete([]);
            setShow(false);
        },
        [setForm]
    );

    const onDeleteRates = React.useCallback(
        toDelete => {
            const toDeleteObjects = toDelete.map(rate => (rate.object ? rate.object.id : rate));
            const newRatesValue = linkedRates.filter(rateId => !toDeleteObjects.includes(rateId));
            onUpdateRates(newRatesValue);
        },
        [linkedRates, onUpdateRates]
    );

    //we get all rates to have the id of all the non-selected rates (right below)
    const [allRates, loadingRates] = RateModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: event.id,
            launch: Boolean(organization.id && event),
            query: {
                limit: 1000
            }
        },
        [organization, event]
    );

    //we filter all rates id on the selected ones we have to get the non-selected list
    const notLinkedRates = React.useMemo(() => {
        if (!allRates) return [];
        const notLinked = allRates.filter(r => !linkedRates.includes(r.id) && r.id);
        return notLinked.map(rate => rate.object.id);
    }, [allRates, linkedRates]);

    //custom rates cell with cross icon (for delete)
    let getTableCells = React.useCallback(
        (hasDelete = false) => {
            return [
                {
                    header: t('ticket.rates.rateName'),
                    renderRate: (rate, { groups }) => {
                        // do not display icon for actual waiting list
                        const displayLinkedIcon =
                            (!waitingListId && rate.waiting_list_rate) || (rate.waiting_list_rate && waitingListId && Number(waitingListId) !== rate.waiting_list_rate.id);
                        return (
                            <div className={Style['cell-group']}>
                                <InheritedProperty
                                    displayIcon={false}
                                    property="name"
                                    object={rate}
                                    handleProperty={name => (
                                        <div className={StyleRM[generateClassNameRateName(rate, groups)]} style={{ display: 'flex', alignItems: 'center' }}>
                                            {name}
                                            {displayLinkedIcon && (
                                                <Tooltip triggerOnHover title={t('ticket.waiting_list.create.already_linked')}>
                                                    <a href={`/ticket/O${organization.id}/waiting_lists/${rate.waiting_list_rate.id}`}>
                                                        <Icon name="link" />
                                                    </a>
                                                </Tooltip>
                                            )}
                                        </div>
                                    )}
                                    template="rate_template"
                                />
                                {hasDelete && <Icon name="cross" onClick={() => onDeleteRates([rate.id])} className={Style['delete-icon']} />}
                            </div>
                        );
                    },
                    renderGroup: (group, onToggle) => {
                        return (
                            <div className={StyleRM['rates-table__cell-group']} onClick={() => onToggle(group)}>
                                {group.parent && <Icon className={StyleRM['rates-table__cell-group-icon-subgroup']} name="link-group" />}
                                <Icon name={group.showRates ? 'folder-open' : 'folder-closed'} style={{ marginRight: '5px' }} />
                                <InheritedProperty displayIcon={false} property="name" object={group} handleProperty={name => <b>{name}</b>} template="group_template" />
                            </div>
                        );
                    }
                }
            ];
        },
        [event, organization, onDeleteRates, waitingListId]
    );

    return (
        <LoadingCard loading={loadingRates}>
            <Modal
                size="large"
                open={show}
                onClose={() => setShow(false)}
                title={t('ticket.ticket_design.create_design.tickets')}
                subtitle={t('ticket.ticket_design.create_design.tickets_description')}
                cancelButton={<Button onClick={() => setShow(false)} label={t('ticket.common.action.cancel')} />}
                submitButton={<Button primary onClick={() => onUpdateRates(linkedRates.concat(selectedRatesAdd))} label={t('ticket.common.action.add')} />}
            >
                <div className={Style['rates-manager-container']}>
                    <RatesManager
                        className={Style['rules-rates-list']}
                        event={event}
                        organization={organization}
                        initialQuery={{
                            organizationId: organization.id,
                            eventId: event.id
                        }}
                        excludeRatesIds={linkedRates}
                        tableCells={getTableCells()}
                        selected={selectedRatesAdd}
                        onRatesSelected={setSelectedRatesAdd}
                        mode={'other'}
                        hideEmptyGroup={true}
                        selectable={true}
                        activeSyncRates={false}
                        activeCacheRates
                        activeCacheGroups
                    />
                </div>
            </Modal>
            {linkedRates.length > 0 ? (
                <RatesManager
                    className={Style['rules-rates-list']}
                    event={event}
                    organization={organization}
                    initialQuery={{
                        organizationId: organization.id,
                        eventId: event.id
                    }}
                    excludeRatesIds={notLinkedRates}
                    tableCells={getTableCells(true)}
                    mode={'other'}
                    hideEmptyGroup={true}
                    selectable={true}
                    activeSyncRates={false}
                    activeCacheRates
                    activeCacheGroups
                    selected={selectedRatesDelete}
                    onRatesSelected={setSelectedRatesDelete}
                >
                    <div className={Style['container-global-actions']}>
                        <SelectButton
                            tooltipTitle={t('ticket.rates.noActionTooltip')}
                            title={selectedRatesDelete.length > 0 ? t('ticket.rates.rateAction', { count: selectedRatesDelete.length }) : t('ticket.common.action.bulk')}
                            objects={selectedRatesDelete}
                        >
                            <Button onClick={() => onDeleteRates(selectedRatesDelete)}>{t('ticket.cross-selling.listVariant.deleteAll')}</Button>
                        </SelectButton>
                        <div className={'container-secondary-actions'}>
                            <SortRatesButton disabled={false} onClick={() => setShow(true)} label={t('ticket.creationEvent.recap.addRates')} icon="plus" />
                        </div>
                    </div>
                </RatesManager>
            ) : (
                <Card.EmptyState
                    title={t('ticket.waiting_list.create.rates_picker.empty.title')}
                    subtitle={t('ticket.waiting_list.create.rates_picker.empty.subtitle')}
                    icon={<Icon name="calendar-star" />}
                    labelButton={t('ticket.waiting_list.create.rates_picker.empty.cta')}
                    onClick={() => setShow(true)}
                />
            )}
        </LoadingCard>
    );
}
